import { useCallback, useEffect, useState } from 'react';
import { api, ApiClientError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useToast } from '../../components/Toast';
import Icon from '../../components/Icon';
import Modal from '../../components/Modal';
import { Badge, EmptyState, PageLoading, TextInput, Toggle } from '../../components/ui';
import { PageHeader } from '../../pages/AppShell';
import type { ExpenseCategory } from './types';
import { rupees } from './types';

/**
 * Expense categories and their rates.
 *
 * Travel Allowance and Daily Allowance are built in — their amount is
 * computed from the rate set here (per km, per day), so a claim never
 * depends on a rep typing the right figure. General categories are the
 * admin's own; a rep enters the amount on those.
 */
function rateLabel(c: ExpenseCategory): string {
  if (c.rate == null) return '—';
  if (c.kind === 'travel_allowance') return `${rupees(c.rate)} / km`;
  if (c.kind === 'daily_allowance') return `${rupees(c.rate)} / day`;
  return rupees(c.rate);
}

const KIND_BADGE: Record<ExpenseCategory['kind'], string> = {
  travel_allowance: 'Travel', daily_allowance: 'Daily', general: 'General',
};

export default function ExpenseCategoriesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const isAdmin = can('company_admin', 'platform_admin');

  const [cats, setCats] = useState<ExpenseCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ExpenseCategory | 'new' | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCats(await api.get<ExpenseCategory[]>('/expenses/categories'));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not load categories.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { void load(); }, [load]);

  async function setActive(c: ExpenseCategory, isActive: boolean) {
    try {
      await api.patch(`/expenses/categories/${c.id}`, { isActive });
      setCats((list) => list.map((x) => (x.id === c.id ? { ...x, isActive } : x)));
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update that.');
    }
  }

  const needsRate = cats.some((c) => c.isSystem && c.rate == null);

  return (
    <>
      <PageHeader
        title="Expense categories"
        subtitle="The kinds of expense your team can claim, and the allowance rates."
        actions={isAdmin ? (
          <button type="button" className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={16} /> Add category
          </button>
        ) : undefined}
      />

      <div className="page col gap-16">
        {needsRate && isAdmin ? (
          <div className="card card-pad" style={{ borderColor: 'var(--warning)', background: 'var(--warning-soft)' }}>
            <span className="row gap-8" style={{ alignItems: 'center' }}>
              <Icon name="alert" size={16} />
              <span className="small">
                Set a rate on Travel and Daily Allowance before your team claims them —
                without it, the allowance can&apos;t be filed.
              </span>
            </span>
          </div>
        ) : null}

        {loading ? <PageLoading /> : cats.length === 0 ? (
          <div className="card">
            <EmptyState icon="list" title="No categories yet" />
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Type</th>
                    <th>Rate</th>
                    <th className="num">Used</th>
                    <th>On the form</th>
                    {isAdmin ? <th /> : null}
                  </tr>
                </thead>
                <tbody>
                  {cats.map((c) => (
                    <tr key={c.id}>
                      <td className="primary">
                        <span className="row gap-8" style={{ alignItems: 'center' }}>
                          {c.name}
                          {c.isSystem ? <Badge tone="neutral">Built in</Badge> : null}
                          {!c.isActive ? <Badge tone="neutral">Hidden</Badge> : null}
                        </span>
                      </td>
                      <td><Badge tone={c.kind === 'general' ? 'neutral' : 'brand'}>{KIND_BADGE[c.kind]}</Badge></td>
                      <td className={c.rate == null && c.isSystem ? 'nowrap' : 'nowrap num'}>
                        {c.rate == null && c.isSystem
                          ? <span style={{ color: 'var(--warning)', fontWeight: 600 }}>Set a rate</span>
                          : rateLabel(c)}
                      </td>
                      <td className="num">{c.claimCount ?? 0}</td>
                      <td>
                        {isAdmin && !c.isSystem
                          ? <Toggle checked={c.isActive} label="" onChange={(v) => void setActive(c, v)} />
                          : (c.isActive ? 'Yes' : 'No')}
                      </td>
                      {isAdmin ? (
                        <td>
                          <button type="button" className="icon-btn" title="Edit"
                            onClick={() => setEditing(c)}>
                            <Icon name="edit" size={15} />
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {editing ? (
        <CategoryDialog
          category={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load(); }}
        />
      ) : null}
    </>
  );
}

function CategoryDialog({ category, onClose, onSaved }: {
  category: ExpenseCategory | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const isSystem = category?.isSystem ?? false;
  const [name, setName] = useState(category?.name ?? '');
  const [rate, setRate] = useState(category?.rate ?? '');

  const rateUnit = category?.kind === 'travel_allowance' ? 'per kilometre'
    : category?.kind === 'daily_allowance' ? 'per day' : null;

  async function save() {
    setBusy(true);
    try {
      if (category) {
        const body: Record<string, unknown> = { name: name.trim() };
        if (isSystem) body.rate = rate.trim() === '' ? null : rate.trim();
        await api.patch(`/expenses/categories/${category.id}`, body);
      } else {
        await api.post('/expenses/categories', { name: name.trim() });
      }
      toast.success(category ? 'Saved.' : `${name.trim()} added.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!category) return;
    setBusy(true);
    try {
      const r = await api.del<{ deactivated: boolean; claimsUsing?: number }>(`/expenses/categories/${category.id}`);
      toast.success(r.deactivated
        ? `${category.name} has ${r.claimsUsing} claim(s) against it, so it was hidden rather than deleted.`
        : `${category.name} removed.`);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiClientError ? err.message : 'Could not remove that.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={category ? category.name : 'Add a category'}
      subtitle={isSystem ? 'A built-in allowance — set its name and rate.' : undefined}
      onClose={onClose}
      footer={(
        <>
          {category && !isSystem ? (
            <button type="button" className="btn btn-danger-ghost" disabled={busy} onClick={() => void remove()}>
              Remove
            </button>
          ) : null}
          <span className="grow" />
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={busy || !name.trim()}
            onClick={() => void save()}>
            {category ? 'Save' : 'Add'}
          </button>
        </>
      )}
    >
      <div className="col gap-12">
        <TextInput label="Name" value={name} placeholder="Fuel"
          onChange={(e) => setName(e.target.value)} />
        {isSystem ? (
          <div className="col gap-4">
            <label className="small" style={{ fontWeight: 600 }}>Rate ({rateUnit})</label>
            <div className="row gap-8" style={{ alignItems: 'center' }}>
              <span className="faint">₹</span>
              <input type="number" className="input" style={{ maxWidth: 140 }} min={0} step="0.01"
                value={rate} placeholder="0.00"
                onChange={(e) => setRate(e.target.value)} />
              <span className="small faint">{rateUnit}</span>
            </div>
            <span className="small faint">
              {category?.kind === 'travel_allowance'
                ? 'A travel claim is the day’s tracked distance × this rate.'
                : 'A daily allowance claim is this fixed amount per day.'}
              {' '}Leave blank to turn the allowance off until you set it.
            </span>
          </div>
        ) : (
          <span className="small faint">
            A general category — the rep enters the amount when they claim it.
          </span>
        )}
      </div>
    </Modal>
  );
}
